import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl, slugify } from "../../lib/admin";

const empty = { title: "", slug: "", content: "", cover_image: "", brand_id: "" };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () =>
    Promise.all([api.get("/blog"), api.get("/brands")]).then(([p, b]) => {
      setPosts(p.data);
      setBrands(b.data);
    });
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing("new");
    setForm(empty);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...empty, ...p, brand_id: p.brand_id || "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.title),
        brand_id: form.brand_id || null,
      };
      if (editing === "new") await api.post("/blog", payload);
      else await api.put(`/blog/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/blog/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const onCover = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm({ ...form, cover_image: url });
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Editorial</div>
          <h1 className="font-display text-4xl tracking-tighter">Blog Posts</h1>
        </div>
        <button onClick={openCreate} className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2" data-testid="blog-create-btn">
          <Plus size={14} /> New Post
        </button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3 font-normal">Title</th>
              <th className="px-4 py-3 font-normal">Slug</th>
              <th className="px-4 py-3 font-normal">Brand</th>
              <th className="px-4 py-3 font-normal">Date</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="px-4 py-3 font-medium">{p.title}</td>
                <td className="px-4 py-3 text-zinc-400">{p.slug}</td>
                <td className="px-4 py-3 text-zinc-400">{brands.find((b) => b.id === p.brand_id)?.name || "All"}</td>
                <td className="px-4 py-3 text-zinc-400">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-white p-2"><Edit2 size={14} /></button>
                  <button onClick={() => remove(p)} className="text-zinc-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No posts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-[#0A0A0A] border border-white/10 p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl">{editing === "new" ? "New Post" : "Edit Post"}</div>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Title"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} className="adm-input" data-testid="blog-form-title" /></Field>
              <Field label="Slug"><input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="adm-input" /></Field>
              <Field label="Brand (or All)">
                <select value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} className="adm-input">
                  <option value="">All / Main</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Cover Image">
                <div className="flex items-center gap-3">
                  {form.cover_image && <img src={form.cover_image} alt="" className="h-10 w-16 object-cover" />}
                  <input type="file" accept="image/*" onChange={(e) => onCover(e.target.files?.[0])} className="text-xs text-zinc-400" />
                </div>
              </Field>
              <Field label="Content" full>
                <textarea required rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="adm-input" data-testid="blog-form-content" />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5">Cancel</button>
              <button type="submit" className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]" data-testid="blog-form-save">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
